import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import { usePublicSettings } from '../context/PublicSettingsContext';
import { FARM_ADDRESS_LINE, FARM_ADDRESS_CITY } from '../constants/address';
import './Contact.css';

const WEDDING_EMAIL = 'coryblossom22@gmail.com';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.6 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const DEFAULT_HOURS = [
  { label: 'Monday - Friday', value: '2:00 PM - 6:00 PM' },
  { label: 'Saturday', value: '10:00 AM - 6:00 PM' },
  { label: 'Sunday', value: 'Closed' },
];

const Contact = () => {
  const [searchParams] = useSearchParams();
  const { settings } = usePublicSettings();
  const hoursLines = settings?.business_hours?.lines || DEFAULT_HOURS;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const topic = searchParams.get('topic');
    if (topic === 'wedding') {
      setFormData(prev => ({
        ...prev,
        subject: 'wedding',
        message: prev.message || 'Hi Cory — I\'m interested in wedding florals. My wedding date is: \nVenue / location: \nApproximate guest count: \nWhat I\'m dreaming of (bouquets, ceremony, reception, etc.): \n',
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const isWeddingInquiry = searchParams.get('topic') === 'wedding' || formData.subject === 'wedding';

  const handleSubmit = (e) => {
    e.preventDefault();
    const subjectLine =
      formData.subject === 'wedding' || searchParams.get('topic') === 'wedding'
        ? 'Wedding floral inquiry — Cory Blossom'
        : `Contact — ${formData.subject || 'General inquiry'}`;
    const body = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\n${formData.message}`;
    window.location.href = `mailto:${WEDDING_EMAIL}?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 4000);
  };

  return (
    <motion.main
      className="contact"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header__bg">
          <img
            src="/background.png"
            alt="Cory Blossom flower farm"
          />
          <div className="page-header__overlay" />
        </div>
        <div className="page-header__content">
          <motion.span
            className="script-font page-header__eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Get in Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Contact
          </motion.h1>
          <motion.p
            className="page-header__sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            Located in the beautiful Finger Lakes region of New York
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact__content">
        <div className="container">
          <div className="contact__grid">
            {/* Contact Info */}
            <AnimatedSection className="contact__info" direction="left">
              <h2>Let's Talk Flowers</h2>
              <p className="contact__intro">
                Have a question about our flowers, need a custom arrangement, or planning
                a special event in the Finger Lakes? We'd love to hear from you!
              </p>
              {isWeddingInquiry && (
                <div className="contact__wedding-callout">
                  <h3>Wedding florals with Cory</h3>
                  <p>
                    All wedding work starts with a conversation. Share your date, venue, and vision — Cory will follow up to discuss consultation, availability, and a custom quote.
                  </p>
                  <a className="contact__mailto" href={`mailto:${WEDDING_EMAIL}?subject=${encodeURIComponent('Wedding floral inquiry — Cory Blossom')}`}>
                    Or email directly: {WEDDING_EMAIL}
                  </a>
                </div>
              )}

              <div className="contact__details">
                <div className="contact__detail-item">
                  <div className="contact__detail-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <h4>Visit Our Farm</h4>
                    <p>
                      Cory Blossom Flower Farm<br />
                      {FARM_ADDRESS_LINE}<br />
                      {FARM_ADDRESS_CITY}
                    </p>
                  </div>
                </div>

                <div className="contact__detail-item">
                  <div className="contact__detail-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <h4>Email Us</h4>
                    <p>coryblossom22@gmail.com</p>
                  </div>
                </div>

                <div className="contact__detail-item">
                  <div className="contact__detail-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                    </svg>
                  </div>
                  <div>
                    <h4>Call Us</h4>
                    <p>
                      <a href="tel:+16073311332">607-331-1332</a> <span className="contact__phone-label">(Cory)</span><br />
                      <a href="tel:+16073313364">607-331-3364</a> <span className="contact__phone-label">(Assistant)</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="contact__hours">
                <h4>Farm Hours</h4>
                <ul>
                  {hoursLines.map((line) => (
                    <li key={line.label}><span>{line.label}</span><span>{line.value}</span></li>
                  ))}
                </ul>
              </div>

            </AnimatedSection>

            {/* Contact Form */}
            <AnimatedSection className="contact__form-wrapper" direction="right">
              {submitted ? (
                <motion.div
                  className="contact__success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  <div className="contact__success-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                </motion.div>
              ) : (
                <form className="contact__form" onSubmit={handleSubmit}>
                  <h3>Send Us a Message</h3>

                  <div className="contact__form-row">
                    <div className="contact__form-group">
                      <label htmlFor="name">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="contact__form-group">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="contact__form-row">
                    <div className="contact__form-group">
                      <label htmlFor="phone">Phone (optional)</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 000-0000"
                      />
                    </div>
                    <div className="contact__form-group">
                      <label htmlFor="subject">Subject</label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a topic</option>
                        <option value="wedding">Wedding Consultation</option>
                        <option value="general">General Inquiry</option>
                        <option value="order">Order Question</option>
                        <option value="event">Event Flowers</option>
                        <option value="custom">Custom Arrangement</option>
                      </select>
                    </div>
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={isWeddingInquiry ? 'Wedding date, venue, guest count, and the florals you have in mind...' : "Tell us about what you're looking for..."}
                      required
                    />
                  </div>

                  <motion.button
                    type="submit"
                    className="btn btn-primary contact__submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Send Message
                  </motion.button>
                </form>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="contact__map-section">
        <div className="container">
          <AnimatedSection>
            <h2 className="section-title">Find Us in the Finger Lakes</h2>
            <p className="section-subtitle">{FARM_ADDRESS_LINE} · {FARM_ADDRESS_CITY}</p>
          </AnimatedSection>
          <AnimatedSection className="contact__map-wrapper">
            <iframe
              title="Cory Blossom - Finger Lakes Region Map"
              src="https://www.google.com/maps?q=180+N+Catharine+St+Montour+Falls+NY+14865&output=embed"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Finger Lakes Feature */}
      <section className="contact__finger-lakes">
        <div className="container">
          <div className="finger-lakes-grid">
            <AnimatedSection className="finger-lakes-info" direction="left">
              <span className="script-font finger-lakes-eyebrow">Our Home</span>
              <h2>The Finger Lakes Region</h2>
              <p>
                Our flower farm is proudly located in New York's stunning Finger Lakes region —
                a world-renowned destination known for its pristine glacial lakes, award-winning
                wineries, rolling farmland, and vibrant local culture.
              </p>
              <p>
                The Finger Lakes' unique microclimate and rich soil create ideal growing
                conditions for the diverse variety of flowers we cultivate. From the shores
                of Seneca Lake to the valleys of Keuka, our blooms carry the essence of
                this extraordinary landscape.
              </p>
              <div className="finger-lakes-highlights">
                <div className="fl-highlight">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                  </svg>
                  <span>Serving Finger Lakes weddings & events</span>
                </div>
                <div className="fl-highlight">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  <span>Farm visits welcome by appointment</span>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection className="finger-lakes-image" direction="right">
              <motion.img
                src="/background.png"
                alt="Cory Blossom Finger Lakes flower farm"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>
    </motion.main>
  );
};

export default Contact;
